import React, { useState, useEffect } from 'react';
import { Plus, Search, CheckCircle, Circle, Trash2, Phone, Mail, FileText, Calendar, Filter, Download } from 'lucide-react';
import { exportActivities } from '../utils/export';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '../services/api';

const ACTIVITY_TYPES = { call: 'Appel', email: 'Email', task: 'Tâche', note: 'Note' };
const ACTIVITY_ICONS = { call: Phone, email: Mail, task: CheckCircle, note: FileText };
const ACTIVITY_COLORS = {
  call: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  email: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  task: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  note: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const emptyForm = { type: 'call', title: '', description: '', deal_id: '', contact_id: '', due_date: '' };

export default function Activities() {
  const [activities, setActivities] = useState([]);
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/activities'), api.get('/deals'), api.get('/contacts')])
      .then(([a, d, c]) => { setActivities(a.data); setDeals(d.data); setContacts(c.data); })
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = activities.filter(a => {
    const matchSearch = !search || `${a.title} ${a.contact_name || ''} ${a.deal_title || ''}`.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'all' || a.type === typeFilter;
    const matchStatus = statusFilter === 'all' || (statusFilter === 'pending' ? !a.completed : a.completed);
    return matchSearch && matchType && matchStatus;
  });

  const handleSave = async () => {
    if (!form.title) { toast.error('Le titre est requis'); return; }
    setSaving(true);
    try {
      const deal = deals.find(d => d.id === form.deal_id);
      const contact = contacts.find(c => c.id === form.contact_id);
      const res = await api.post('/activities', {
        ...form,
        deal_title: deal?.title || '',
        contact_name: contact ? `${contact.first_name} ${contact.last_name}` : '',
      });
      setActivities(prev => [res.data, ...prev]);
      setShowDialog(false);
      setForm(emptyForm);
      toast.success('Activité créée');
    } catch { toast.error('Erreur lors de la création'); }
    finally { setSaving(false); }
  };

  const handleComplete = async (id) => {
    try {
      await api.put(`/activities/${id}/complete`);
      setActivities(prev => prev.map(a => a.id === id ? { ...a, completed: true } : a));
      toast.success('Activité complétée');
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/activities/${id}`);
      setActivities(prev => prev.filter(a => a.id !== id));
      toast.success('Activité supprimée');
    } catch { toast.error('Erreur'); }
  };

  const pendingCount = activities.filter(a => !a.completed).length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Activités</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {pendingCount} en attente · {activities.length} total
          </p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setShowDialog(true); }}
          className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white h-9 px-4 text-sm font-medium rounded-lg active:scale-95"
          style={{ transition: 'background-color 150ms ease, transform 100ms ease' }} data-testid="add-activity-btn">
          <Plus size={15} className="mr-1.5" /> Nouvelle activité
        </Button>
        <Button onClick={() => exportActivities(filtered)} variant="outline" className="h-9 px-3 text-sm border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="export-activities-btn">
          <Download size={14} className="mr-1.5" /> Exporter CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-0 max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm border-gray-200 bg-white dark:bg-[#1A1D23]" data-testid="activities-search" />
        </div>
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="h-9 bg-white dark:bg-[#1A1D23] border border-gray-200 dark:border-gray-700 p-1">
            <TabsTrigger value="pending" className="text-xs h-7 px-3" data-testid="filter-pending">En attente</TabsTrigger>
            <TabsTrigger value="completed" className="text-xs h-7 px-3" data-testid="filter-completed">Complétées</TabsTrigger>
            <TabsTrigger value="all" className="text-xs h-7 px-3" data-testid="filter-all">Toutes</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36 h-9 text-sm border-gray-200 bg-white dark:bg-[#1A1D23]" data-testid="type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            {Object.entries(ACTIVITY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Activities list */}
      {loading ? (
        <div className="p-8 text-center text-gray-400">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-12 text-center">
          <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
            <Calendar size={20} className="text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Aucune activité</p>
          <Button onClick={() => setShowDialog(true)} size="sm" className="mt-3 bg-[#1B99E5] hover:bg-[#0f86cc] text-white">
            <Plus size={13} className="mr-1.5" /> Ajouter
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(act => {
            const Icon = ACTIVITY_ICONS[act.type] || FileText;
            return (
              <div key={act.id}
                className={`flex items-center gap-4 bg-white dark:bg-[#1A1D23] rounded-xl border ${act.completed ? 'border-gray-100 dark:border-gray-800 opacity-70' : 'border-gray-200 dark:border-gray-700'} px-4 py-3 group`}
                style={{ transition: 'opacity 200ms ease' }}
                data-testid={`activity-row-${act.id}`}
              >
                <button
                  onClick={() => !act.completed && handleComplete(act.id)}
                  className="flex-shrink-0"
                  disabled={act.completed}
                  data-testid={`complete-btn-${act.id}`}
                >
                  {act.completed
                    ? <CheckCircle size={18} className="text-[#1B99E5]" />
                    : <Circle size={18} className="text-gray-300 hover:text-[#1B99E5]" style={{ transition: 'color 150ms ease' }} />
                  }
                </button>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${ACTIVITY_COLORS[act.type] || 'bg-gray-100 text-gray-500'}`}>
                  <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase">{ACTIVITY_TYPES[act.type] || act.type}</span>
                    {act.deal_title && <span className="text-xs text-gray-400">· {act.deal_title}</span>}
                    {act.contact_name && <span className="text-xs text-gray-400">· {act.contact_name}</span>}
                  </div>
                  <p className={`text-sm font-medium mt-0.5 ${act.completed ? 'line-through text-gray-400' : 'text-gray-900 dark:text-white'}`}>{act.title}</p>
                  {act.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{act.description}</p>}
                </div>
                {act.due_date && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 flex-shrink-0 hidden sm:flex">
                    <Calendar size={12} />
                    <span>{act.due_date}</span>
                  </div>
                )}
                <button
                  onClick={() => handleDelete(act.id)}
                  className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
                  style={{ transition: 'opacity 150ms ease' }}
                  data-testid={`delete-activity-${act.id}`}
                >
                  <Trash2 size={14} className="text-gray-400 hover:text-red-500" style={{ transition: 'color 150ms ease' }} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md" data-testid="activity-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nouvelle activité</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Type</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="new-activity-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ACTIVITY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Titre *</Label>
              <Input placeholder="Ex: Appel de qualification" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="new-activity-title" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Description</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Deal associé</Label>
                <Select value={form.deal_id} onValueChange={v => setForm({ ...form, deal_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>{deals.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Date</Label>
                <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white" data-testid="save-activity-btn">
              {saving ? 'Création...' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
